import { getLocalData, StorageKeyOptionsEnum } from 'common/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOrganization } from 'src/pages/organization';

interface UseWebSocketOptions {
  url: string;
  onMessage?: (data: MessageEvent) => void;
  onConnect?: () => void;
  enabled?: boolean;
  reconnectAttempts?: number;
  reconnectInterval?: number;
}

type WebSocketStatus = 'connecting' | 'connected' | 'disconnected';

const getAccessToken = () => {
  const accessData = getLocalData(StorageKeyOptionsEnum.AccessData);
  return accessData?.access_token ?? '';
};

const useWebSocket = ({
  url,
  onMessage,
  onConnect,
  enabled = true,
  reconnectAttempts = 10,
  reconnectInterval = 3000,
}: UseWebSocketOptions) => {
  const { currentOrganizationId } = useOrganization();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const reconnectCountRef = useRef(0);
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');

  onMessageRef.current = onMessage;
  onConnectRef.current = onConnect;

  const buildUrl = useCallback(() => {
    const token = getAccessToken();
    if (!token || !currentOrganizationId) return null;

    const authValue = encodeURIComponent('Bearer ' + token);
    const orgValue = encodeURIComponent(currentOrganizationId);

    return `${url}?Authorization=${authValue}&organizationId=${orgValue}`;
  }, [url, currentOrganizationId]);

  useEffect(() => {
    if (!enabled || !currentOrganizationId || !url) return;

    const connect = () => {
      const wsUrl = buildUrl();
      if (!wsUrl) return;

      setStatus('connecting');
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectCountRef.current = 0;
        setStatus('connected');
        onConnectRef.current?.();
      };

      ws.onmessage = (event) => {
        onMessageRef.current?.(event);
      };

      ws.onclose = () => {
        setStatus('disconnected');

        if (reconnectCountRef.current < reconnectAttempts) {
          reconnectCountRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(connect, reconnectInterval);
        }
      };
    };

    connect();

    return () => {
      reconnectCountRef.current = reconnectAttempts;
      wsRef.current?.close();
      clearTimeout(reconnectTimeoutRef.current);
    };
  }, [
    enabled,
    url,
    currentOrganizationId,
    buildUrl,
    reconnectAttempts,
    reconnectInterval,
  ]);

  return { status };
};

export { useWebSocket };
export type { UseWebSocketOptions, WebSocketStatus };
